<template>
  <div class="contract-editor-page">
    <div class="editor-toolbar">
      <div class="toolbar-left">
        <el-button size="small" icon="el-icon-arrow-left" @click="goBack">返回列表</el-button>
        <span class="contract-title">{{ contractTitle }}</span>
        <span v-if="contractNo" class="contract-no">{{ contractNo }}</span>
      </div>
      <div class="toolbar-center editor-hint">
        公章/签名已自动插入文末。如需微调位置，选中图片拖动，保存后再导出 PDF。
      </div>
      <div class="toolbar-right">
        <el-button size="small" :loading="reloadLoading" @click="reloadEditor">重新加载</el-button>
        <el-button size="small" type="primary" :loading="exportLoading" @click="exportPdf">导出 PDF</el-button>
      </div>
    </div>

    <OnlyOfficeEditor
      v-if="ooConfig"
      ref="ooEditor"
      :key="ooEditorKey"
      :config="ooConfig"
      :fullscreen="true"
      class="contract-oo-editor"
      @ready="onOoReady"
      @saved="onOoSaved"
    />
    <div v-else-if="loading" class="editor-placeholder">正在打开合同 Word…</div>
    <div v-else-if="loadError" class="editor-placeholder is-error">
      {{ loadError }}
      <el-button size="small" style="margin-top:12px" @click="loadEditor">重试</el-button>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api'
import { userParams } from '@/utils/user'
import OnlyOfficeEditor from '@/components/OnlyOfficeEditor.vue'

const API_BASE = process.env.VUE_APP_API_BASE || ''

export default {
  name: 'ContractEditor',
  components: { OnlyOfficeEditor },
  data() {
    return {
      contractId: 0,
      contractTitle: '',
      contractNo: '',
      ooConfig: null,
      ooEditorKey: '',
      loading: false,
      loadError: '',
      reloadLoading: false,
      exportLoading: false
    }
  },
  watch: {
    '$route.params.id'() {
      this.initPage()
    }
  },
  mounted() {
    this.initPage()
  },
  methods: {
    initPage() {
      this.contractId = parseInt(this.$route.params.id, 10) || 0
      if (this.contractId <= 0) {
        this.loadError = '无效的合同 ID'
        return
      }
      this.loadMeta().then(() => this.loadEditor())
    },
    async loadMeta() {
      try {
        const res = await api.get('yn_contract_get', { id: this.contractId })
        const data = res.data || {}
        if (data.error) {
          this.loadError = data.error
          return
        }
        const c = data.contract || {}
        this.contractTitle = c.template_name || c.title || '合同'
        this.contractNo = c.contract_no || ''
      } catch (e) {
        this.loadError = '加载合同信息失败'
      }
    },
    async loadEditor() {
      if (this.contractId <= 0) return
      this.loading = true
      this.loadError = ''
      this.ooConfig = null
      try {
        const res = await api.get('yn_contract_oo_open', { id: this.contractId })
        const cfg = res.data || {}
        if (cfg.error) {
          this.loadError = cfg.error
          return
        }
        this.ooEditorKey = 'c-' + this.contractId + '-' + Date.now()
        this.ooConfig = cfg
      } catch (e) {
        this.loadError = '打开编辑器失败'
      } finally {
        this.loading = false
      }
    },
    async reloadEditor() {
      this.reloadLoading = true
      try {
        await this.loadEditor()
        this.$message.success('已重新加载')
      } finally {
        this.reloadLoading = false
      }
    },
    onOoReady() {
      this.$message.success('合同已打开，可直接改字号、签章位置')
    },
    onOoSaved() {
      this.$message.success('已保存')
    },
    goBack() {
      this.$router.push('/contracts')
    },
    async exportPdf() {
      this.exportLoading = true
      try {
        if (this.$refs.ooEditor && this.$refs.ooEditor.triggerForceSave) {
          this.$refs.ooEditor.triggerForceSave()
          await new Promise(r => setTimeout(r, 1200))
        }
        const qs = new URLSearchParams(userParams({ yn_contract_export_pdf: '1', id: this.contractId }))
        const url = API_BASE + '/yn_api.php?' + qs.toString()
        const res = await fetch(url)
        if (!res.ok) throw new Error('HTTP ' + res.status)
        const ct = (res.headers.get('Content-Type') || '').toLowerCase()
        if (ct.includes('json') || ct.includes('text/plain')) {
          const text = await res.text()
          let msg = text
          try { msg = JSON.parse(text).error || text } catch (e) { /* keep */ }
          throw new Error(msg || '导出失败')
        }
        const blob = await res.blob()
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = (this.contractNo ? this.contractNo + '_' : '') + (this.contractTitle || 'contract') + '.pdf'
        a.click()
        URL.revokeObjectURL(a.href)
        this.$message.success('PDF 已下载')
      } catch (e) {
        this.$message.error(e.message || '导出失败，请先保存 Word 后再试')
      } finally {
        this.exportLoading = false
      }
    }
  }
}
</script>

<style scoped>
.contract-editor-page {
  margin: -20px;
  height: calc(100vh - 56px);
  display: flex;
  flex-direction: column;
  background: #fff;
}
.editor-toolbar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid #ebeef5;
  background: #fff;
}
.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 0 1 auto;
}
.toolbar-center.editor-hint {
  flex: 1 1 auto;
  margin: 0 16px;
  font-size: 12px;
  color: #909399;
  text-align: center;
  line-height: 1.4;
}
.contract-title {
  font-size: 15px;
  font-weight: 500;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 360px;
}
.contract-no {
  font-size: 12px;
  color: #909399;
}
.toolbar-right {
  display: flex;
  gap: 8px;
}
.contract-oo-editor {
  flex: 1 1 auto;
  min-height: 0;
}
.contract-oo-editor >>> .oo-editor-wrap {
  height: 100% !important;
  min-height: 0 !important;
  border: none;
}
.editor-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 15px;
}
.editor-placeholder.is-error {
  color: #f56c6c;
}
</style>
